import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { resetUser } from 'src/auth/utils/resetUser';
import { Application } from 'switch-shared/modules/crud/application/application.entity';
import { UpdateDto } from 'switch-shared/modules/crud/base/base.service';

import { NetworkState } from '../../constants/network-state';
import ApplicationApiService from '../../services/switch-rest-api/rest-application.service';
import { ALL_SELECTION_ID } from '../../utils/selection';
import { userStorage } from '../../utils/userStorage';
import {
  updateSelectedApplicationGroupId,
  updateSelectedApplicationGroupIdSilent,
} from '../application-groups/application-group.slice';

const applicationApiService = new ApplicationApiService();

const createAllApplicationsOption = (): Application => ({
  _id: ALL_SELECTION_ID,
  id: ALL_SELECTION_ID,
  name: 'All Applications',
  applicationGroupId: '',
  createdAt: new Date(),
  updatedAt: new Date(),
});

export const fetchApplications = createAsyncThunk(
  'applications/fetchApplications',
  async (applicationGroupId: string) => {
    try {
      const res = await applicationApiService.getApplications({
        applicationGroupId,
        page: 0,
        limit: 1000,
      });
      return res;
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

export const createApplication = createAsyncThunk(
  'applications/createApplication',
  async (newAppObject: {}) => {
    try {
      const res = await applicationApiService.createApplication(newAppObject);
      return res;
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

export const updateApplication = createAsyncThunk(
  'applications/updateApplication',
  async ({ id, app }: { id: string; app: UpdateDto<Application> }) => {
    try {
      await applicationApiService.updateApplication(id, app);
      return {
        ...app,
        _id: id,
      };
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

export const deleteApplication = createAsyncThunk(
  'applications/deleteApplication',
  async (id: string) => {
    try {
      const res = await applicationApiService.deleteApplication(id);
      return {
        ...res,
        _id: id,
      };
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

export const uniqueHumanIdCheck = createAsyncThunk(
  'applications/uniqueHumanIdCheck',
  async (data: { collectionName: string; humanId: string }) => {
    try {
      const res = await applicationApiService.uniqueHumanIdCheck(data);
      return res;
    } catch (error: any) {
      throw new Error(error.message, { cause: error });
    }
  }
);

interface applicationsSliceInterface {
  applicationNetworkStatus: {
    applications: NetworkState;
    isUpdatingApplication: NetworkState;
    isCreatingApplication: NetworkState;
    isDeletingApplication: NetworkState;
  };
  errors: {
    isUpdatingApplication: string | undefined;
    isCreatingApplication: string | undefined;
    isDeletingApplication: string | undefined;
  };
  applications: Application[];
  applicationsCount: number;
  selectedAppId: string | undefined;
  manualApplicationChange: boolean;
  selectedApplicationId: string;
}

const initialState: applicationsSliceInterface = {
  applicationNetworkStatus: {
    applications: NetworkState.NOT_STARTED,
    isUpdatingApplication: NetworkState.NOT_STARTED,
    isCreatingApplication: NetworkState.NOT_STARTED,
    isDeletingApplication: NetworkState.NOT_STARTED,
  },
  errors: {
    isUpdatingApplication: '',
    isCreatingApplication: '',
    isDeletingApplication: '',
  },
  applications: [],
  applicationsCount: 0,
  selectedAppId: undefined,
  selectedApplicationId: '',
  manualApplicationChange: false,
};

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    updateSelectedApplicationId(state, action) {
      userStorage.setItem('selectedAppId', JSON.stringify(action.payload));
      state.manualApplicationChange = true;
      state.selectedAppId = action.payload;
    },
    updateSelectedApplicationIdSilent(state, action) {
      userStorage.setItem('selectedAppId', JSON.stringify(action.payload));
      state.manualApplicationChange = false;
      state.selectedAppId = action.payload;
    },
    updateSelectedAppId(state, action) {
      state.selectedApplicationId = action.payload;
    },
    resetAppUpdateAndCreateState(state) {
      state.applicationNetworkStatus.isUpdatingApplication =
        NetworkState.NOT_STARTED;
      state.applicationNetworkStatus.isCreatingApplication =
        NetworkState.NOT_STARTED;
    },
    setApplicationsToAll(state) {
      userStorage.setItem('selectedAppId', JSON.stringify(ALL_SELECTION_ID));
      state.applicationNetworkStatus.applications = NetworkState.SUCCESS;
      state.applicationsCount = 1;
      state.applications = [createAllApplicationsOption()];
      state.selectedAppId = ALL_SELECTION_ID;
      state.manualApplicationChange = false;
    },
  },
  extraReducers: (builder) => {
    // builder.addCase(signOutSuccess, (state) => initialState);
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.applicationNetworkStatus.applications = NetworkState.PENDING;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applicationNetworkStatus.applications = NetworkState.SUCCESS;

        const storedId = userStorage.getItem('selectedAppId');

        if (storedId) {
          state.selectedAppId = JSON.parse(storedId);
        } else {
          if (action.payload?.value?.totalRecords === 1) {
            state.selectedAppId = action.payload?.value.data[0]._id;
            userStorage.setItem(
              'selectedAppId',
              JSON.stringify(state.selectedAppId)
            );
          } else {
            state.selectedAppId = ALL_SELECTION_ID;
            userStorage.setItem(
              'selectedAppId',
              JSON.stringify(ALL_SELECTION_ID)
            );
          }
        }

        state.applicationsCount = action.payload?.value?.totalRecords || 0;
        state.applications = [
          createAllApplicationsOption(),
          ...(action.payload?.value?.data ?? []),
        ];
      })
      .addCase(fetchApplications.rejected, (state) => {
        toast.error('Error fetching Applications');
        state.applicationNetworkStatus.applications = NetworkState.ERROR;
      })
      .addCase(createApplication.pending, (state) => {
        toast.info('Creating Application...');
        state.applicationNetworkStatus.isCreatingApplication =
          NetworkState.PENDING;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        toast.dismiss();
        state.applicationNetworkStatus.isCreatingApplication =
          NetworkState.SUCCESS;
        toast.success('Application successfully created');
        state.applications = _.concat(
          action.payload?.value,
          state.applications
        );
        state.applicationNetworkStatus.applications = NetworkState.NOT_STARTED;
      })
      .addCase(createApplication.rejected, (state) => {
        toast.error('Error creating application');
        state.applicationNetworkStatus.isCreatingApplication =
          NetworkState.ERROR;
      })
      .addCase(updateApplication.pending, (state) => {
        toast.info('Updating Application...');
        state.applicationNetworkStatus.isUpdatingApplication =
          NetworkState.PENDING;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        toast.dismiss();
        state.applications = state.applications.map((application) =>
          application._id === action.payload?._id
            ? ({ ...application, ...action.payload } as any)
            : application
        );
        state.selectedAppId = undefined;
        state.applicationNetworkStatus.isUpdatingApplication =
          NetworkState.SUCCESS;
        toast.success('Application successfully updated');
        state.applicationNetworkStatus.applications = NetworkState.NOT_STARTED;
      })
      .addCase(updateApplication.rejected, (state) => {
        toast.error('There was an error saving the application');
        state.applicationNetworkStatus.isUpdatingApplication =
          NetworkState.ERROR;
        state.selectedAppId = undefined;
      })
      .addCase(deleteApplication.pending, (state) => {
        toast.info('Deleting Application...');
        state.applicationNetworkStatus.isDeletingApplication =
          NetworkState.PENDING;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        toast.dismiss();
        const storedId = userStorage.getItem('selectedAppId');

        // If the currently selected application is deleted
        if (storedId === JSON.stringify(action.payload._id)) {
          userStorage.removeItem('selectedAppId');
        }

        toast.success('Application successfully deleted');

        state.applicationNetworkStatus.isDeletingApplication =
          NetworkState.SUCCESS;
        state.applicationNetworkStatus.applications = NetworkState.NOT_STARTED;
      })
      .addCase(deleteApplication.rejected, (state) => {
        toast.error('There was an error deleting the application');
        state.applicationNetworkStatus.isDeletingApplication =
          NetworkState.ERROR;
        state.selectedAppId = undefined;
      })
      .addCase(updateSelectedApplicationGroupId, (_state, action) => {
        if (action.payload === ALL_SELECTION_ID) {
          const nextState = {
            ...initialState,
            applicationNetworkStatus: {
              ...initialState.applicationNetworkStatus,
              applications: NetworkState.SUCCESS,
            },
            applicationsCount: 1,
            applications: [createAllApplicationsOption()],
            selectedAppId: ALL_SELECTION_ID,
          };
          return nextState as applicationsSliceInterface;
        }
        return initialState;
      })
      .addCase(updateSelectedApplicationGroupIdSilent, (_state, action) => {
        if (action.payload === ALL_SELECTION_ID) {
          const nextState = {
            ...initialState,
            applicationNetworkStatus: {
              ...initialState.applicationNetworkStatus,
              applications: NetworkState.SUCCESS,
            },
            applicationsCount: 1,
            applications: [createAllApplicationsOption()],
            selectedAppId: ALL_SELECTION_ID,
          };
          return nextState as applicationsSliceInterface;
        }
        return initialState;
      })
      .addCase(resetUser.fulfilled, () => {
        return initialState;
      });
  },
});

export const {
  updateSelectedApplicationId,
  updateSelectedApplicationIdSilent,
  updateSelectedAppId,
  resetAppUpdateAndCreateState,
  setApplicationsToAll,
} = applicationsSlice.actions;

export default applicationsSlice.reducer;
